/**
 * Analytics and tracking utilities for Google Analytics and GTM
 * Centralized tracking functions to avoid code duplication
 */

/**
 * Get email domain information and test domain detection
 * Extracts email from localStorage profile data and checks if it's a test domain
 * @returns {Object} Object with email_domain (string|null) and is_test_data (boolean)
 */
const getEmailDomainInfo = () => {
  try {
    const profile = JSON.parse(localStorage.getItem("profile"));
    const email = profile?.Emailid;
    if (!email) return { email_domain: null, is_test_data: false };
    
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return { email_domain: null, is_test_data: false };
    
    const testDomains = ['tmail', 'yopmail', 'unitedwecare', 'oz'];
    const isTestDomain = testDomains.some(testDomain => 
      domain.includes(testDomain)
    );
    
    return { email_domain: domain, is_test_data: isTestDomain };
  } catch {
    return { email_domain: null, is_test_data: false };
  }
};

/**
 * Track clinical note creation event
 * @param {string|number} userId - The user ID of the person creating the note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteCreation = async (userId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_create_note',
      event_category: 'Clinical Note',
      event_label: 'Create Clinical Note',
      value: 10,
      User_ID: userId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_create_note fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    // gtag will queue if script hasn't loaded yet, then process when loaded
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_create_note', {
        event_category: 'Clinical Note',
        event_label: 'Create Clinical Note',
        value: 10,
        User_ID: userId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note patient selection event
 * @param {string|number} userId - The user ID of the person selecting the patient
 * @param {string|number} patientId - The ID of the selected patient
 * @returns {Promise<void>}
 */
export const trackClinicalNoteSelectPatient = async (userId, patientId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_create_note_select_patient',
      event_category: 'Clinical Note',
      event_label: 'create_note_select_patient',
      patient_id: patientId,
      value: 8,
      User_ID: userId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_create_note_select_patient fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_create_note_select_patient', {
        event_category: 'Clinical Note',
        event_label: 'create_note_select_patient',
        patient_id: patientId,
        value: 8,
        User_ID: userId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note start recording event
 * @param {string|number} userId - The user ID of the person starting the recording
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @param {string} language - The language code (e.g., 'en', 'es') to be converted to full name
 * @param {number} durationSec - Duration in seconds (0 at start)
 * @returns {Promise<void>}
 */
export const trackClinicalNoteStartRecording = async (userId, patientId, noteId, language, durationSec = 0) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Map language code to full name
    const languageMap = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ar': 'Arabic',
      'hi': 'Hindi',
      // Add more mappings as needed
    };
    
    const languageName = languageMap[language] || language || 'English';
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_start_recording',
      event_category: 'Clinical Note',
      event_label: 'start_recording',
      value: 10,
      duration_sec: durationSec,
      language: languageName,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_start_recording fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_start_recording', {
        event_category: 'Clinical Note',
        event_label: 'start_recording',
        value: 10,
        duration_sec: durationSec,
        language: languageName,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track Ask Sam mic clicked event
 * @param {string|number} userId - The user ID of the person clicking the mic
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackAskSamMicClicked = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_ask_sam_mic_clicked',
      event_category: 'Ask Sam',
      event_label: 'Ask Sam Mic',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_ask_sam_mic_clicked fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_ask_sam_mic_clicked', {
        event_category: 'Ask Sam',
        event_label: 'Ask Sam Mic',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track Ask Sam file uploaded event
 * @param {string|number} userId - The user ID of the person uploading the file
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackAskSamFileUploaded = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_ask_sam_file_uploaded',
      event_category: 'Ask Sam',
      event_label: 'Ask Sam File Upload',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_ask_sam_file_uploaded fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_ask_sam_file_uploaded', {
        event_category: 'Ask Sam',
        event_label: 'Ask Sam File Upload',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track Ask Sam button click event
 * @param {string|number} userId - The user ID of the person clicking the Ask Sam button
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackAskSamButtonClick = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_ask_sam_button_click',
      event_category: 'Ask Sam',
      event_label: 'Ask Sam Button',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_ask_sam_button_click fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_ask_sam_button_click', {
        event_category: 'Ask Sam',
        event_label: 'Ask Sam Button',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note graph view event
 * @param {string|number} userId - The user ID of the person viewing the graph
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteGraphView = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_graph_view',
      event_category: 'Graph',
      event_label: 'Patient Graph',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_graph_view fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_graph_view', {
        event_category: 'Graph',
        event_label: 'Patient Graph',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note assistant view event
 * @param {string|number} userId - The user ID of the person viewing the assistant
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteAssistantView = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_assistant_view',
      event_category: 'Assistant',
      event_label: 'Assistant View',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_assistant_view fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_assistant_view', {
        event_category: 'Assistant',
        event_label: 'Assistant View',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track Ask Sam message sent event
 * @param {string|number} userId - The user ID of the person sending the message
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackAskSamMessageSent = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_ask_sam_message_sent',
      event_category: 'Ask Sam',
      event_label: 'Ask Sam Message Sent',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_ask_sam_message_sent fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_ask_sam_message_sent', {
        event_category: 'Ask Sam',
        event_label: 'Ask Sam Message Sent',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note delete event
 * @param {string|number} userId - The user ID of the person deleting the note
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteDelete = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_create_delete',
      event_category: 'Clinical Note',
      event_label: 'Delete',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_create_delete fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_create_delete', {
        event_category: 'Clinical Note',
        event_label: 'Delete',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note submit event
 * @param {string|number} userId - The user ID of the person submitting the note
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteSubmit = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_create_submit',
      event_category: 'Clinical Note',
      event_label: 'Submit',
      value: 10,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_create_submit fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_create_submit', {
        event_category: 'Clinical Note',
        event_label: 'Submit',
        value: 10,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note save and exit event
 * @param {string|number} userId - The user ID of the person saving and exiting
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteSaveAndExit = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_create_save_exit',
      event_category: 'Clinical Note',
      event_label: 'Save and Exit',
      value: 5,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_create_save_exit fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_create_save_exit', {
        event_category: 'Clinical Note',
        event_label: 'Save and Exit',
        value: 5,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note accept caution event
 * @param {string|number} userId - The user ID of the person accepting the caution
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteAcceptCaution = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_create_accept_caution',
      event_category: 'Clinical Note',
      event_label: 'Accept_Caution',
      value: 5,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_create_accept_caution fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_create_accept_caution', {
        event_category: 'Clinical Note',
        event_label: 'Accept_Caution',
        value: 5,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note click send transcription event
 * @param {string|number} userId - The user ID of the person clicking send transcription
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteClickSendTranscription = async (userId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_click_send_transcription',
      event_category: 'Clinical Note',
      event_label: 'Send Transcription',
      value: 5,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_click_send_transcription fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_click_send_transcription', {
        event_category: 'Clinical Note',
        event_label: 'Send Transcription',
        value: 5,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track clinical note upload file event
 * @param {string|number} userId - The user ID of the person uploading the file
 * @param {string|number} patientId - The ID of the patient
 * @param {string|number} noteId - The ID of the clinical note
 * @returns {Promise<void>}
 */
export const trackClinicalNoteUploadFile = async (userId, patientId, noteId) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'clinical_note_upload_file',
      event_category: 'Clinical Note',
      event_label: 'Upload Button',
      value: 5,
      patient_id: patientId,
      User_ID: userId,
      note_id: noteId,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] clinical_note_upload_file fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'clinical_note_upload_file', {
        event_category: 'Clinical Note',
        event_label: 'Upload Button',
        value: 5,
        patient_id: patientId,
        User_ID: userId,
        note_id: noteId,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

/**
 * Track page view event (for virtual page views in SPAs)
 * @param {string} pagePath - The path of the page (e.g., '/clinical/notes')
 * @param {string} pageTitle - The title of the page (optional)
 * @returns {Promise<void>}
 */
export const trackPageView = async (pagePath, pageTitle = '') => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Always push to dataLayer first (for GTM)
    const eventData = {
      event: 'page_view',
      page_path: pagePath,
      page_title: pageTitle || document.title,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log('[ANALYTICS] page_view fired:', eventData);
    
    window.dataLayer.push(eventData);
    
    // Send event immediately via gtag (preferred) if available
    // Note: send_page_view is only valid in config, not in event calls
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle || document.title,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    await new Promise(resolve => setTimeout(resolve, 200));
    
  } catch (error) {
    console.error('Error sending page view event:', error);
  }
};

/**
 * Generic event tracking function for future use
 * @param {string} eventName - The name of the event
 * @param {Object} eventData - Event data object to send
 * @param {Object} options - Optional configuration (e.g., { flushDelay: 200 })
 * @returns {Promise<void>}
 */
export const trackEvent = async (eventName, eventData = {}, options = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    // Get email domain information
    const { email_domain, is_test_data } = getEmailDomainInfo();
    
    // Merge event name with event data and email domain info
    const fullEventData = {
      event: eventName,
      ...eventData,
      email_domain: email_domain,
      is_test_data: is_test_data
    };
    
    // console.log(`[ANALYTICS] ${eventName} fired:`, fullEventData);
    
    window.dataLayer.push(fullEventData);
    
    // Send event immediately via gtag (preferred) if available
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, {
        ...eventData,
        email_domain: email_domain,
        is_test_data: is_test_data
      });
    }
    
    // Force flush: wait a moment for event to be sent before any navigation
    const flushDelay = options.flushDelay || 200;
    await new Promise(resolve => setTimeout(resolve, flushDelay));
    
  } catch (error) {
    console.error('Error sending event:', error);
  }
};

